import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { register } from '../features/auth/authSlice';
import { Button, GlassCard } from '../components/ui';
import BrandLogo from '../components/BrandLogo';

const inputClassName =
  'w-full rounded-btn border border-white/10 bg-bg px-4 py-3 text-sm text-body placeholder:text-muted/70 transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

const RegisterScreen = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userInfo, loading, error } = useSelector((state) => state.auth);

  const redirect = location.search ? location.search.split('=')[1] : '/';

  useEffect(() => {
    if (userInfo) {
      navigate(redirect);
    }
  }, [navigate, userInfo, redirect]);

  const submitHandler = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage(t('auth.register.passwordMismatch', 'Passwords do not match'));
    } else {
      dispatch(register({ name, email, password }));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6 pt-28 pb-16">
      <GlassCard hover={false} className="w-full max-w-md p-6 md:p-8">
        <div className="mb-6 flex justify-center">
          <BrandLogo to="/" className="h-12" />
        </div>
        <h1 className="mb-6 text-2xl font-semibold tracking-tight text-body">{t('auth.register.title')}</h1>
        {message && <Message variant="danger">{message}</Message>}
        {error && <Message variant="danger">{error}</Message>}
        {loading && <Loader />}
        <form onSubmit={submitHandler} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-muted mb-1">
              {t('auth.register.name')}
            </label>
            <input
              type="text"
              id="name"
              placeholder={t('auth.register.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClassName}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-muted mb-1">
              {t('auth.register.email')}
            </label>
            <input
              type="email"
              id="email"
              placeholder={t('auth.register.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClassName}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-muted mb-1">
              {t('auth.register.password')}
            </label>
            <input
              type="password"
              id="password"
              placeholder={t('auth.register.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClassName}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-muted mb-1">
              {t('auth.register.confirmPassword')}
            </label>
            <input
              type="password"
              id="confirmPassword"
              placeholder={t('auth.register.confirmPassword')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClassName}
            />
          </div>

          <Button type="submit" className="w-full">
            {t('auth.register.submit')}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-muted">
            {t('auth.register.hasAccount')}{' '}
            <Link 
              to={redirect ? `/login?redirect=${redirect}` : '/login'}
              className="text-body underline-offset-4 hover:underline"
            >
              {t('auth.register.login')}
            </Link>
          </p>
        </div>
      </GlassCard>
    </div>
  );
};

export default RegisterScreen;
